<!--
  - Copyright (c) 2025. Haodong JU
  -->

<template>
  <v-dialog
    v-model="show"
    max-width="900px"
    persistent
  >
    <v-card>
      <div class="headline d-flex pa-4">
        <div class="d-flex align-center">
          <v-avatar
            v-if="award && award.image"
            class="mr-3"
            size="40"
          >
            <v-img
              :src="award.image"
              alt="Award Image"
            />
          </v-avatar>
          <div>
            {{ award ? award.description : '奖励详情' }}
          </div>
        </div>
        <v-spacer />
        <v-btn
          icon
          @click="close"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>

      <v-divider />

      <v-tabs
        v-model="activeTab"
        background-color="transparent"
        color="info"
      >
        <v-tab>详细信息</v-tab>
        <v-tab>奖励进度</v-tab>
        <v-tab>操作日志</v-tab>
      </v-tabs>

      <div>
        <v-tabs-items v-model="activeTab">
          <!-- 详细信息 Tab -->
          <v-tab-item>
            <v-card flat>
              <v-card-text>
                <div
                  v-if="award"
                  class="award-details"
                >
                  <!-- Award Metrics -->
                  <div class="award-metrics mb-4">
                    <div class="metric-card">
                      <div class="metric-icon">
                        <v-icon color="info">
                          mdi-trophy-award
                        </v-icon>
                      </div>
                      <div class="metric-content">
                        <div class="metric-value">
                          {{ award.awardMax }}
                        </div>
                        <div class="metric-label">
                          可兑奖数量
                        </div>
                      </div>
                    </div>

                    <div class="metric-card">
                      <div class="metric-icon">
                        <v-icon color="info">
                          mdi-flag-checkered
                        </v-icon>
                      </div>
                      <div class="metric-content">
                        <div class="metric-value">
                          {{ award.awardStart }}
                        </div>
                        <div class="metric-label">
                          起始值
                        </div>
                      </div>
                    </div>

                    <div class="metric-card">
                      <div class="metric-icon">
                        <v-icon :color="award.active ? 'success' : 'error'">
                          {{ award.active ? 'mdi-check-circle' : 'mdi-close-circle' }}
                        </v-icon>
                      </div>
                      <div class="metric-content">
                        <div class="metric-value">
                          {{ award.active ? '激活' : '未激活' }}
                        </div>
                        <div class="metric-label">
                          状态
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Award Details -->
                  <div class="details-grid">
                    <div class="detail-card">
                      <div class="detail-label">
                        <v-icon
                          small
                          color="info"
                          class="mr-1"
                        >
                          mdi-information-outline
                        </v-icon>
                        描述
                      </div>
                      <div class="detail-value">
                        {{ award.description }}
                      </div>
                    </div>

                    <div class="detail-card">
                      <div class="detail-label">
                        <v-icon
                          small
                          color="info"
                          class="mr-1"
                        >
                          mdi-package-variant
                        </v-icon>
                        资产
                      </div>
                      <div class="detail-value">
                        {{ award.assetName || '未设置' }}
                      </div>
                    </div>

                    <div class="detail-card">
                      <div class="detail-label">
                        <v-icon
                          small
                          color="info"
                          class="mr-1"
                        >
                          mdi-refresh
                        </v-icon>
                        可重复
                      </div>
                      <div class="detail-value">
                        <v-chip
                          small
                          :color="award && award.repeatable ? 'success' : 'grey'"
                        >
                          {{ award && award.repeatable ? '是' : '否' }}
                        </v-chip>
                      </div>
                    </div>

                    <div class="detail-card">
                      <div class="detail-label">
                        <v-icon
                          small
                          color="info"
                          class="mr-1"
                        >
                          mdi-swap-horizontal
                        </v-icon>
                        兑换类型
                      </div>
                      <div class="detail-value">
                        {{ getClaimTypeLabel(award.claimType) }}
                      </div>
                    </div>

                    <div class="detail-card">
                      <div class="detail-label">
                        <v-icon
                          small
                          color="info"
                          class="mr-1"
                        >
                          mdi-currency-usd
                        </v-icon>
                        最小订单金额
                      </div>
                      <div class="detail-value">
                        {{ award.minOrderAmount }}
                      </div>
                    </div>

                    <div class="detail-card">
                      <div class="detail-label">
                        <v-icon
                          small
                          color="info"
                          class="mr-1"
                        >
                          mdi-calendar-clock
                        </v-icon>
                        积分清除天数
                      </div>
                      <div class="detail-value">
                        {{ award.pointClearAfterLastUsageDays }}
                      </div>
                    </div>

                    <div class="detail-card">
                      <div class="detail-label">
                        <v-icon
                          small
                          color="info"
                          class="mr-1"
                        >
                          mdi-plus-circle-outline
                        </v-icon>
                        超额累积
                      </div>
                      <div class="detail-value">
                        <v-chip
                          small
                          :color="award && award.allowExcessAccumulation ? 'success' : 'grey'"
                        >
                          {{ award && award.allowExcessAccumulation ? '是' : '否' }}
                        </v-chip>
                      </div>
                    </div>
                  </div>
                </div>
              </v-card-text>
            </v-card>
          </v-tab-item>

          <!-- 奖励进度 Tab -->
          <v-tab-item>
            <v-card flat>
              <v-card-text>
                <div
                  v-if="loading"
                  class="d-flex justify-center align-center pa-4"
                >
                  <v-progress-circular
                    indeterminate
                    color="info"
                  />
                </div>
                <div
                  v-else-if="progressData.length === 0"
                  class="empty-state pa-4"
                >
                  <div class="text-center">
                    <v-icon
                      size="64"
                      color="grey lighten-1"
                    >
                      mdi-chart-timeline-variant
                    </v-icon>
                    <h3 class="mt-2 grey--text text--darken-1">
                      暂无奖励进度数据
                    </h3>
                    <p class="grey--text">
                      当用户开始累积积分时，这里将显示他们的进度
                    </p>
                  </div>
                </div>
                <div v-else>
                  <v-data-table
                    :headers="progressHeaders"
                    :items="progressData"
                    :items-per-page="10"
                    class="elevation-1 progress-table"
                  >
                    <template #item.progress="{ item }">
                      <div class="progress-container">
                        <v-progress-linear
                          :value="(item.progress / award.awardMax) * 100"
                          height="20"
                          color="info"
                          class="rounded-lg"
                        >
                          <template #default>
                            <strong>{{ item.progress }} / {{ award.awardMax }}</strong>
                          </template>
                        </v-progress-linear>
                        <div class="progress-percentage">
                          {{ Math.round((item.progress / award.awardMax) * 100) }}%
                        </div>
                      </div>
                    </template>
                    <template #item.status="{ item }">
                      <v-chip
                        :color="item && item.status ? getStatusColor(item.status) : 'grey'"
                        small
                      >
                        {{ item && item.status ? item.status : '' }}
                      </v-chip>
                    </template>
                  </v-data-table>
                </div>
              </v-card-text>
            </v-card>
          </v-tab-item>

          <!-- 操作日志 Tab -->
          <v-tab-item>
            <v-card flat>
              <v-card-text>
                <div
                  v-if="loading"
                  class="d-flex justify-center align-center pa-4"
                >
                  <v-progress-circular
                    indeterminate
                    color="info"
                  />
                </div>
                <div
                  v-else-if="logs.length === 0"
                  class="empty-state pa-4"
                >
                  <div class="text-center">
                    <v-icon
                      size="64"
                      color="grey lighten-1"
                    >
                      mdi-history
                    </v-icon>
                    <h3 class="mt-2 grey--text text--darken-1">
                      暂无操作日志
                    </h3>
                    <p class="grey--text">
                      当有人对此奖励进行操作时，这里将显示操作记录
                    </p>
                  </div>
                </div>
                <div
                  v-else
                  class="logs-container"
                >
                  <v-timeline dense>
                    <v-timeline-item
                      v-for="(log, index) in logs"
                      :key="index"
                      :color="getLogColor(log.action)"
                      small
                      class="log-item"
                    >
                      <v-card class="log-card elevation-1">
                        <v-card-text class="pa-3">
                          <div class="d-flex align-center mb-2">
                            <v-icon
                              small
                              :color="getLogColor(log.action)"
                              class="mr-2"
                            >
                              {{ getLogIcon(log.action) }}
                            </v-icon>
                            <div class="font-weight-bold">
                              {{ log.action }}
                            </div>
                            <v-spacer />
                            <div class="text-caption">
                              {{ log.timestamp }}
                            </div>
                          </div>
                          <div class="log-details">
                            {{ log.details }}
                          </div>
                        </v-card-text>
                      </v-card>
                    </v-timeline-item>
                  </v-timeline>
                </div>
              </v-card-text>
            </v-card>
          </v-tab-item>
        </v-tabs-items>
      </div>

      <v-divider />

      <v-card-actions class="pa-4">
        <v-btn
          color="info"
          text
          @click="editAward"
        >
          <v-icon left>
            mdi-pencil
          </v-icon>
          编辑奖励
        </v-btn>
        <v-spacer />
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { AwardClaimType } from '@/model/dataLayer/api/MembershipSystem/AwardManagement'
import { hillo } from 'hillo'
import { cloudV2Url } from '@/model/dataLayer/api/SaaS/SaaSUtils'
import GlobalConfig from '@/model/global/GlobalSettings'
import { getAllUser } from '@/model/dataLayer/api/MembershipSystem/UserMembership'
import { getAssetDetail } from '@/model/dataLayer/api/MembershipSystem/Asset'
import { useBaseFormDialogStore } from '@/model/baseForm/baseFormDialogStore'
import { showSuccessMessage, showErrorMessage } from '@/model/utils/utils'
import IKUtils from 'innerken-js-utils'
import { createAwardFormSchema } from '@/model/baseForm/awardFormSchemas'

export default {
  name: 'AwardDetailDialog',
  props: {
    value: {
      type: Boolean,
      default: false
    },
    awardId: {
      type: String,
      default: null
    }
  },
  data () {
    return {
      show: false,
      activeTab: 0,
      award: null,
      loading: false,
      progressData: [],
      logs: [],
      progressHeaders: [
        { text: '用户', value: 'username' },
        { text: '进度', value: 'progress' },
        { text: '状态', value: 'status' }
      ]
    }
  },
  watch: {
    value (val) {
      this.show = val
      if (val && this.awardId) {
        this.loadAwardDetails()
      }
    },
    show (val) {
      this.$emit('input', val)
      if (!val) {
        this.activeTab = 0
      }
    },
    activeTab (val) {
      if (val === 1 && this.awardId) {
        this.loadProgressData()
      } else if (val === 2 && this.awardId) {
        this.loadLogs()
      }
    }
  },
  methods: {
    close () {
      this.show = false
    },
    async editAward () {
      if (!this.award) return

      try {
        // Create a deep copy of the award to avoid modifying the original
        const awardToEdit = IKUtils.deepCopy(this.award)

        // Prepare the data for the form
        const formData = {
          description: awardToEdit.description,
          awardMax: awardToEdit.awardMax,
          awardStart: awardToEdit.awardStart,
          assetId: awardToEdit.assetId,
          active: awardToEdit.active,
          repeatable: awardToEdit.repeatable,
          minOrderAmount: awardToEdit.minOrderAmount,
          claimType: awardToEdit.claimType,
          pointClearAfterLastUsageDays: awardToEdit.pointClearAfterLastUsageDays,
          allowExcessAccumulation: awardToEdit.allowExcessAccumulation
        }

        const store = useBaseFormDialogStore()

        // Create the form schema with edit mode
        const schema = createAwardFormSchema({
          isEdit: true,
          award: this.award
        })

        // Open the dialog and wait for the result
        const result = await store.editItem(schema, formData)

        // Handle the result
        if (result) {
          // Prepare the updated award data
          const updatedAward = {
            ...this.award,
            description: result.description,
            awardMax: result.awardMax,
            awardStart: result.awardStart,
            assetId: result.assetId,
            claimType: result.claimType,
            minOrderAmount: result.minOrderAmount,
            pointClearAfterLastUsageDays: result.pointClearAfterLastUsageDays,
            repeatable: result.repeatable,
            allowExcessAccumulation: result.allowExcessAccumulation,
            active: result.active
          }

          // Save the updated award
          try {
            const path = 'membership/award'
            await hillo.jsonPost(cloudV2Url + path + '/save', updatedAward)
            showSuccessMessage('奖励更新成功！')

            // Reload the award details
            await this.loadAwardDetails()

            // Emit the edit event to notify the parent component
            this.$emit('edit', this.award)
          } catch (error) {
            console.error('Error updating award:', error)
            showErrorMessage('更新奖励失败: ' + (error.message || '未知错误'))
          }
        }
      } catch (error) {
        console.error('Error in editAward:', error)
        showErrorMessage('编辑奖励时出错: ' + (error.message || '未知错误'))
      }
    },
    async loadAwardDetails () {
      if (!this.awardId) return

      console.log('Loading award details for awardId:', this.awardId)

      this.loading = true
      try {
        // Load award details
        const path = 'membership/award'
        const awards = await hillo.get(cloudV2Url + path + '/byBlId/' + GlobalConfig.blId)
        console.log('All awards:', awards)

        // Use loose equality (==) to handle different types (String vs Number)
        this.award = awards.find(a => String(a.id) === this.awardId)
        console.log('Found award:', this.award, 'for awardId:', this.awardId)

        if (this.award) {
          // Check if awardInfo exists
          if (!this.award.awardInfo) {
            console.error('awardInfo is missing from award data:', this.award)
            // Set default values or use existing properties
            this.award.description = this.award.description || ''
            this.award.awardMax = this.award.awardMax || 0
            this.award.awardStart = this.award.awardStart || 0
            this.award.assetId = this.award.assetId || null
            this.award.assetName = this.award.assetName || ''
            this.award.claimType = this.award.claimType || ''
            this.award.minOrderAmount = this.award.minOrderAmount || 0
            this.award.pointClearAfterLastUsageDays = this.award.pointClearAfterLastUsageDays || 0
            this.award.repeatable = this.award.repeatable || false
            this.award.allowExcessAccumulation = this.award.allowExcessAccumulation || false
          } else {
            // Process award data from awardInfo
            this.award.description = this.award.awardInfo.description
            this.award.awardMax = this.award.awardInfo.awardMax
            this.award.awardStart = this.award.awardInfo.awardStart
            this.award.assetId = this.award.awardInfo.assetId
            this.award.assetName = this.award.assetName || '' // Keep existing assetName or set default
            this.award.claimType = this.award.awardInfo.claimType
            this.award.minOrderAmount = this.award.awardInfo.minOrderAmount
            this.award.pointClearAfterLastUsageDays = this.award.awardInfo.pointClearAfterLastUsageDays
            this.award.repeatable = this.award.awardInfo.repeatable
            this.award.allowExcessAccumulation = this.award.awardInfo.allowExcessAccumulation
          }

          // If assetId is set but assetName is not, try to load the asset name
          if (this.award.assetId && !this.award.assetName) {
            try {
              console.log('Loading asset details for assetId:', this.award.assetId)
              const assetDetails = await getAssetDetail(this.award.assetId)
              if (assetDetails && assetDetails.asset) {
                console.log('Asset details loaded:', assetDetails.asset)
                this.award.assetName = assetDetails.asset.name || ''
              }
            } catch (error) {
              console.error('Error loading asset details:', error)
            }
          }

          // Load progress data if on progress tab
          if (this.activeTab === 1) {
            await this.loadProgressData()
          }

          // Load logs if on logs tab
          if (this.activeTab === 2) {
            await this.loadLogs()
          }
        }
      } catch (error) {
        console.error('Error loading award details:', error)
      } finally {
        this.loading = false
      }
    },
    async loadProgressData () {
      if (!this.awardId) return

      try {
        const path = 'membership/award/'
        console.log('Loading progress data for awardId:', this.awardId)
        const progressData = await hillo.get(cloudV2Url + path + 'progress/blId/' + GlobalConfig.blId)
        console.log('All progress data:', progressData)

        // Use loose equality (==) or String conversion to handle different types (String vs Number)
        const filteredProgressData = progressData.filter(p => String(p.awardTypeId) === String(this.awardId))
        console.log('Filtered progress data:', filteredProgressData)

        // Fetch all users to get usernames
        const users = await getAllUser()
        console.log('All users:', users)

        // Add username to each progress item
        this.progressData = filteredProgressData.map(progress => {
          const user = users.find(u => u.userId === progress.userId)
          console.log('User for progress item:', user, 'userId:', progress.userId)
          return {
            ...progress,
            username: user ? user.username : '未知用户'
          }
        })
        console.log('Final progress data:', this.progressData)
      } catch (error) {
        console.error('Error loading progress data:', error)
      }
    },
    async loadLogs () {
      // This would be implemented if there's an API for logs
      // For now, using mock data
      this.logs = [
        {
          action: '创建奖励',
          timestamp: new Date().toLocaleString(),
          details: '创建了新的奖励项目'
        }
      ]
    },
    getClaimTypeLabel (value) {
      const type = AwardClaimType.find(t => t.value === value)
      return type ? type.label : value
    },
    getStatusColor (status) {
      switch (status) {
        case 'ACTIVE':
          return 'success'
        case 'PENDING':
          return 'warning'
        case 'COMPLETED':
          return 'info'
        default:
          return 'grey'
      }
    },
    getLogColor (action) {
      switch (action) {
        case '创建奖励':
          return 'success'
        case '更新奖励':
          return 'info'
        case '删除奖励':
          return 'error'
        default:
          return 'grey'
      }
    },
    getLogIcon (action) {
      switch (action) {
        case '创建奖励':
          return 'mdi-plus-circle'
        case '更新奖励':
          return 'mdi-pencil'
        case '删除奖励':
          return 'mdi-delete'
        default:
          return 'mdi-information'
      }
    }
  }
}
</script>

<style scoped>
.award-metrics {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 24px;
}

.metric-card {
  flex: 1;
  min-width: 150px;
  background-color: #f5f5f5;
  border-radius: 8px;
  padding: 16px;
  display: flex;
  align-items: center;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  transition: transform 0.2s, box-shadow 0.2s;
}

.metric-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.metric-icon {
  margin-right: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background-color: rgba(0, 0, 0, 0.05);
}

.metric-content {
  flex: 1;
}

.metric-value {
  font-size: 24px;
  font-weight: bold;
}

.metric-label {
  font-size: 14px;
  color: rgba(0, 0, 0, 0.6);
}

/* Progress tab styles */
.empty-state {
  background-color: #f9f9f9;
  border-radius: 8px;
  padding: 32px;
  text-align: center;
}

.progress-table {
  margin-top: 16px;
  border-radius: 8px;
  overflow: hidden;
}

.progress-container {
  display: flex;
  align-items: center;
  gap: 12px;
}

.progress-percentage {
  min-width: 40px;
  font-weight: bold;
  color: #2196F3;
}

/* Details tab styles */
.award-details {
  animation: fadeIn 0.3s ease-in-out;
}

.details-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 16px;
  margin-top: 24px;
}

.detail-card {
  background-color: #f9f9f9;
  border-radius: 8px;
  padding: 16px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  transition: transform 0.2s, box-shadow 0.2s;
}

.detail-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.detail-label {
  display: flex;
  align-items: center;
  font-size: 14px;
  color: rgba(0, 0, 0, 0.6);
  margin-bottom: 8px;
}

.detail-value {
  font-size: 16px;
  font-weight: 500;
}

/* Logs tab styles */
.logs-container {
  padding: 8px;
}

.log-item {
  margin-bottom: 16px;
}

.log-card {
  border-radius: 8px;
  transition: transform 0.2s, box-shadow 0.2s;
}

.log-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1) !important;
}

.log-details {
  color: rgba(0, 0, 0, 0.6);
  font-size: 14px;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
